import React from 'react';

interface BuilderLayoutProps {
  /** Top toolbar bar content (rendered inside flexRow container). */
  toolbar?: React.ReactNode;
  /** Left palette sidebar content. */
  palette: React.ReactNode;
  /** Canvas / main content area (fills remaining space). */
  children: React.ReactNode;
  /** Optional fixed right panel (e.g. properties panel). Pass null to hide. */
  panel?: React.ReactNode;
  /** Width in px of the left palette sidebar. Default 200. */
  paletteWidth?: number;
  /** Width in px of the optional right panel. Default 340. */
  panelWidth?: number;
}

/**
 * BuilderLayout — the three-panel shell shared by all visual flow builders.
 *
 *  ┌────────────────── toolbar ──────────────────────┐
 *  │  palette  │       canvas (children)       │ panel│
 *  └───────────┴───────────────────────────────┴──────┘
 */
const BuilderLayout: React.FC<BuilderLayoutProps> = ({
  toolbar,
  palette,
  children,
  panel,
  paletteWidth = 200,
  panelWidth = 340,
}) => {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      {/* Top toolbar */}
      {toolbar && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 16px',
            borderBottom: '1px solid var(--mg-border)',
            background: 'var(--mg-bg-elevated)',
            flexShrink: 0,
            flexWrap: 'wrap',
          }}
        >
          {toolbar}
        </div>
      )}

      {/* Body row */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left palette */}
        <div
          style={{
            width: paletteWidth,
            borderRight: '1px solid var(--mg-border)',
            background: 'var(--mg-bg-elevated)',
            overflowY: 'auto',
            flexShrink: 0,
          }}
        >
          {palette}
        </div>

        {/* Canvas area */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {children}
        </div>

        {/* Optional right panel */}
        {panel != null && (
          <div
            style={{
              width: panelWidth,
              borderLeft: '1px solid var(--mg-border)',
              background: 'var(--mg-bg-elevated)',
              overflowY: 'auto',
              flexShrink: 0,
            }}
          >
            {panel}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuilderLayout;
